import { type Component, createMemo, type JSX, mergeProps, Show, splitProps } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import Icon from '~/components/Icon'
import { MdiAlert, MdiAlertCircle, MdiCheckCircle, MdiInformation } from '~/components/icons'
import type { AlertPropsType } from '~/types/Alert'
import Button from './Button'

/**
 * Componente Alert al estilo minimal UI
 *
 * @example
 * ```tsx
 * <Alert variant="primary">Esta es una alerta informativa</Alert>
 * <Alert variant="success" title="Éxito">Operación completada con éxito</Alert>
 * <Alert variant="danger" outlined>Error: No se pudo completar la operación</Alert>
 * ```
 */
const Alert: Component<AlertPropsType> = props => {
  const merged = mergeProps({ variant: 'primary', closable: false, outlined: false }, props as Partial<AlertPropsType>)
  const [local, others] = splitProps(merged, ['variant', 'title', 'closable', 'onClose', 'icon', 'outlined', 'children', 'class', 'classList'])

  const defaultIcons: Record<string, Component<JSX.SvgSVGAttributes<SVGSVGElement> & { size?: string | number; color?: string; class?: string }> | string> = {
    primary: MdiInformation,
    secondary: 'mdi:information-outline',
    success: MdiCheckCircle,
    danger: MdiAlertCircle,
    warning: MdiAlert,
    info: 'mdi:information-outline'
  }

  const variantStyles: Record<string, { bg: string; border: string; text: string; icon: string }> = {
    primary: {
      bg: local.outlined ? 'bg-transparent' : 'bg-primary-soft',
      border: 'border-primary',
      text: 'text-on-primary-soft',
      icon: 'text-primary'
    },
    secondary: {
      bg: local.outlined ? 'bg-transparent' : 'bg-primary-soft',
      border: 'border-primary',
      text: 'text-on-primary-soft',
      icon: 'text-primary'
    },
    success: {
      bg: local.outlined ? 'bg-transparent' : 'bg-success-soft',
      border: 'border-success',
      text: 'text-on-success-soft',
      icon: 'text-success'
    },
    danger: {
      bg: local.outlined ? 'bg-transparent' : 'bg-danger-soft',
      border: 'border-danger',
      text: 'text-on-danger-soft',
      icon: 'text-danger'
    },
    warning: {
      bg: local.outlined ? 'bg-transparent' : 'bg-warning-soft',
      border: 'border-warning',
      text: 'text-on-warning-soft',
      icon: 'text-warning'
    },
    info: {
      bg: local.outlined ? 'bg-transparent' : 'bg-info-soft',
      border: 'border-info',
      text: 'text-on-info-soft',
      icon: 'text-info'
    }
  }

  const currentStyle = variantStyles[local.variant]

  const alertClasses = createMemo(() => twMerge('flex items-start p-4 mb-4 rounded-lg', currentStyle.bg, local.outlined ? `border ${currentStyle.border}` : '', currentStyle.text, local.class))
  const iconClasses = createMemo(() => twMerge(`w-5 h-5`, currentStyle.icon))

  return (
    <div class={alertClasses()} classList={local.classList} role="alert" {...others}>
      <div class="shrink-0 mr-3">
        {(() => {
          const icon = local.icon || defaultIcons[local.variant]
          if (typeof icon === 'string') {
            return <Icon icon={icon} class={iconClasses()} />
          } else {
            return icon({ class: iconClasses() })
          }
        })()}
      </div>
      <div class="flex-1 mr-2">
        <Show when={local.title}>
          <h3 class="text-sm font-medium mb-1">{local.title}</h3>
        </Show>
        <div class="text-sm">{local.children}</div>
      </div>
      <Show when={local.closable}>
        <Button icon="material-symbols-light:close" size="sm" variant="text" onClick={local.onClose} />
      </Show>
    </div>
  )
}

export default Alert
